import { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { Plus, Trash2, AlertCircle, MoveHorizontal, ArrowLeftRight, ArrowRight, Files, ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { formatHours, formatCurrency } from '@/lib/budget-utils';
import { fetchRoles, upsertCostCentreBudget, fetchCostCentreBudgetsByProject, getDataMode, type RoleOption } from '@/lib/data-service';
import type { ResourceBudget, RoleBudget, PersonBudget, PhaseBudgetResource } from '@/types/budget.types';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';
import { FillDialog, type FillType } from './fill-dialog'; // Original modal implementation (preserved)
import { FillSheet } from './fill-sheet'; // New sheet implementation with expand/collapse
import { AISuggestion } from './ai-suggestion';
import { ShiftBudgetSheet } from './shift-budget-sheet';
import { normaliseMonthlyHours } from '@/lib/monthly-hours-utils';
// import { ShiftBudgetDialog } from './shift-budget-dialog'; // Original modal - preserved for reference
// Drag and drop imports removed - reordering disabled
// import {
//   DndContext,
//   closestCenter,
//   PointerSensor,
//   useSensor,
//   useSensors,
//   type DragEndEvent,
// } from '@dnd-kit/core';
// import {
//   arrayMove,
//   SortableContext,
//   useSortable,
//   verticalListSortingStrategy,
// } from '@dnd-kit/sortable';
// import { CSS } from '@dnd-kit/utilities';

interface EditResourceBudgetFormProps {
  projectId: number;
  resourceBudget: ResourceBudget;
  onChange: (changes: ResourceBudget) => void;
  /** Fires when a cell edit is committed (on blur), signalling the page to snapshot undo history */
  onCellCommit?: () => void;
  selectedYear?: string;
  projectStartDate?: string;
  projectEndDate?: string;
  /** Cost Centre mode — when true, renders per-cost-centre grouped role rows */
  isCostCentreMode?: boolean;
  /** Flat list of cost centres for Cost Centre mode */
  costCentres?: CostCentre[];
  /** Hierarchical cost centres for Cost Centre mode structure view */
  hierarchicalCostCentres?: HierarchicalCostCentre[];
  /** When true, shows hierarchical/structure view in Cost Centre mode */
  isStructureView?: boolean;
}

// Fallback role options used only when API fetch fails
const FALLBACK_ROLE_OPTIONS = [
  'Project Manager',
  'Senior Consultant',
  'Consultant',
  'Developer',
  'Senior Developer',
  'QA Analyst',
  'QA Engineer',
  'Business Analyst',
  'Technical Architect',
  'UX Designer',
  'Scrum Master',
];

// Month names for columns
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// NumberInput component that handles 2dp formatting properly with max value validation
const NumberInput = ({ value, onChange, onCommit, maxValue = 999999.99, onFocus, ...props }: { value: number; onChange: (value: number) => void; onCommit?: () => void; maxValue?: number; onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void; [key: string]: any }) => {
  const [displayValue, setDisplayValue] = useState(value.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    let numValue = parseFloat(displayValue) || 0;
    
    // Enforce maximum value
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    const formattedValue = parseFloat(numValue.toFixed(2));
    setDisplayValue(formattedValue.toFixed(2));
    // Only call onChange if the value actually changed to avoid false "unsaved changes"
    if (formattedValue !== value) {
      onChange(formattedValue);
    }
    onCommit?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow typing numbers, decimal point, and empty string
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      
      // Debounced onChange to enable toolbar buttons while typing
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new debounced timer (300ms delay to allow for slow typing)
      debounceTimerRef.current = setTimeout(() => {
        let numValue = parseFloat(val) || 0;
        // Enforce maximum value
        if (numValue > maxValue) {
          numValue = maxValue;
        }
        const formattedValue = parseFloat(numValue.toFixed(2));
        // Only call onChange if the value actually changed
        if (formattedValue !== value) {
          onChange(formattedValue);
        }
      }, 300);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

// Helper function to format numbers with thousand separators
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseNumber = (value: string): number => {
  // Remove any commas and parse as float
  const cleaned = value.replace(/,/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

// Props interface for SortableRow - moved outside to prevent recreation on every render
interface SortableRowProps {
  role: RoleBudget;
  index: number;
  selectedCell: { roleIndex: number; monthIndex: number | null } | null;
  setSelectedCell: (cell: { roleIndex: number; monthIndex: number | null } | null) => void;
  setSelectedRows: (rows: Set<number>) => void;
  updateRole: (index: number, field: keyof RoleBudget, value: any) => void;
  updateMonthlyHours: (roleIndex: number, monthIndex: number, value: number) => void;
  handleFillClick: (roleIndex: number, monthIndex: number) => void;
  isSelected: boolean;
  onToggleSelection: (roleId: number) => void;
  visibleMonths: number[];
  monthKeys: string[];
  onCellCommit?: () => void;
}

// Regular Row component - drag and drop removed
const RegularRow = memo(function RegularRow({
  role,
  index,
  selectedCell,
  setSelectedCell,
  setSelectedRows,
  updateRole,
  updateMonthlyHours,
  handleFillClick,
  isSelected,
  onToggleSelection,
  visibleMonths,
  monthKeys,
  onCellCommit,
}: SortableRowProps) {
  const monthlyHours = role.monthlyHours || {};
  const isCellSelected = selectedCell?.roleIndex === index;

  return (
    <TableRow 
      className={`group hover:!bg-transparent ${isCellSelected ? 'bg-primary/5' : ''} ${isSelected ? 'bg-primary/10' : ''}`}
    >
      {/* Checkbox - Frozen */}
      <TableCell className={`px-2 py-1 w-[40px] sticky left-0 z-10 ${isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'}`}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelection(role.id)}
          aria-label={`Select ${role.role}`}
          onClick={(e) => e.stopPropagation()}
        />
      </TableCell>
      
      {/* Role Dropdown - Frozen */}
      <TableCell className={`px-2 py-1 w-[200px] sticky left-[40px] z-10 border-r ${isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'}`}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Combobox
                  options={(() => {
                    // Include the current role if it's not in the predefined list
                    const options = FALLBACK_ROLE_OPTIONS.map((option) => ({
                      value: option,
                      label: option,
                    }));
                    if (role.role && !FALLBACK_ROLE_OPTIONS.includes(role.role)) {
                      options.unshift({ value: role.role, label: role.role });
                    }
                    return options;
                  })()}
                  value={role.role}
                  onValueChange={(value) => updateRole(index, 'role', value)}
                  placeholder="Select role"
                  searchPlaceholder="Search roles..."
                  emptyMessage="No role found."
                  className="h-9"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{role.role}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>

      {/* Monthly Hours */}
      {visibleMonths.map((monthIndex) => {
        const hours = monthlyHours[monthKeys[monthIndex]] || 0;
        const isCellSelected = isSelected && selectedCell?.monthIndex === monthIndex;
        return (
          <TableCell 
            key={monthIndex} 
            className="p-2 w-[100px]"
          >
            <div 
              className="relative group/cell"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                setSelectedCell({ roleIndex: index, monthIndex });
                setSelectedRows(new Set()); // Clear row selection when selecting a cell
              }}
            >
              <NumberInput
                value={hours}
                onChange={(value) => updateMonthlyHours(index, monthIndex, value)}
                onCommit={onCellCommit}
                maxValue={999999.99}
                className={`w-full h-9 px-2 text-sm text-center ${
                  selectedCell?.roleIndex === index && selectedCell?.monthIndex === monthIndex ? 'ring-2 ring-primary' : ''
                }`}
                placeholder="0.00"
              />
              {/* Fill Right Arrow - COMMENTED OUT - Moved to toolbar button */}
              {/* Validation: hours > 0 && monthIndex < totalMonths - 1 */}
              {/* {hours > 0 && monthIndex < totalMonths - 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover/cell:opacity-100 transition-opacity absolute -right-7 top-1/2 -translate-y-1/2"
                  onClick={() => handleFillClick(index, monthIndex)}
                  title="Fill right"
                  tabIndex={-1}
                >
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )} */}
            </div>
          </TableCell>
        );
      })}

      {/* Project Total - Frozen to Right */}
      <TableCell className={`p-2 w-[120px] border-l-2 border-border ${isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'}`}>
        <Input
          value={formatNumber(role.budgetHours)}
          readOnly
          className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
          title={formatNumber(role.budgetHours)}
        />
      </TableCell>
    </TableRow>
  );
});

// ── Cost Centre mode: localStorage helpers ────────────────────────────────
// Per-cost-centre monthly hours: cc-resource-budget-{projectId}-{ccCode}
const CC_RB_KEY = (projectId: number, ccCode: string) =>
  `cc-resource-budget-${projectId}-${ccCode}`;

interface CcResourceBudget {
  /** roleKey (String(role.id)) → Record<"YYYY-MM", hours> */
  roleMonthlyHours: Record<string, Record<string, number>>;
  /** roleKey → human-readable role name (e.g. "Senior Developer") */
  roleNames: Record<string, string>;
  /** roleKey → bill rate */
  roleRates: Record<string, number>;
}

function loadCcResourceBudget(projectId: number, ccCode: string): CcResourceBudget | null {
  if (getDataMode() === 'live') return null;
  try {
    const raw = localStorage.getItem(CC_RB_KEY(projectId, ccCode));
    if (raw) return JSON.parse(raw) as CcResourceBudget;
  } catch { /* ignore */ }
  return null;
}

function saveCcResourceBudget(projectId: number, ccCode: string, data: CcResourceBudget) {
  if (getDataMode() === 'live') return;
  // Write-through to localStorage for immediate UI consistency
  try {
    localStorage.setItem(CC_RB_KEY(projectId, ccCode), JSON.stringify(data));
  } catch { /* ignore */ }
}

// Debounced API persist for resource centre budgets
const ccResourceBudgetSaveTimers: Record<string, ReturnType<typeof setTimeout>> = {};

function persistCcResourceBudgetToApi(costCentreId: number, ccCode: string, monthlyBudgets: Record<string, number>) {
  const timerKey = `resource-${ccCode}`;
  if (ccResourceBudgetSaveTimers[timerKey]) clearTimeout(ccResourceBudgetSaveTimers[timerKey]);
  ccResourceBudgetSaveTimers[timerKey] = setTimeout(() => {
    upsertCostCentreBudget(costCentreId, 'resource', monthlyBudgets).catch((err) => {
      console.error(`[ResourceBudget] Failed to persist budget for CC ${ccCode}:`, err);
    });
  }, 800);
}

// ── Cost Centre mode: buildTree / flattenHierarchy ────────────────────────
// (Pre-emptive Issue 4 fix — always call buildTree before flattenHierarchy)

interface FlatCcRbNode extends HierarchicalCostCentre {
  level: number;
  hasChildren: boolean;
}

function buildRbTree(items: HierarchicalCostCentre[]): HierarchicalCostCentre[] {
  const map = new Map<number, HierarchicalCostCentre>();
  const roots: HierarchicalCostCentre[] = [];

  items.forEach(item => {
    map.set(item.id, { ...item, children: [], level: 0 });
  });

  items.forEach(item => {
    const node = map.get(item.id)!;
    if (item.parentId !== undefined && item.parentId !== null && map.has(item.parentId)) {
      const parent = map.get(item.parentId)!;
      if (!parent.children) parent.children = [];
      parent.children.push(node);
      node.level = (parent.level || 0) + 1;
    } else {
      roots.push(node);
    }
  });

  return roots;
}

function flattenRbHierarchy(nodes: HierarchicalCostCentre[], level = 0): FlatCcRbNode[] {
  const result: FlatCcRbNode[] = [];
  for (const node of nodes) {
    result.push({
      ...node,
      level,
      hasChildren: !!(node.children && node.children.length > 0),
    });
    if (node.children && node.children.length > 0) {
      result.push(...flattenRbHierarchy(node.children, level + 1));
    }
  }
  return result;
}

// ── Helper: build project months (same as Sales Budget form) ─────────────
function buildProjectMonthsRb(projectStartDate?: string, projectEndDate?: string): string[] {
  if (!projectStartDate || !projectEndDate) {
    const year = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(year, i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }
  const months: string[] = [];
  const start = new Date(projectStartDate);
  const end = new Date(projectEndDate);
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (current <= last) {
    months.push(`${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`);
    current.setMonth(current.getMonth() + 1);
  }
  return months;
}

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── ResourceCostCentreBudgetView ──────────────────────────────────────────
// Renders cost centres as rows, role rows as children — mirrors Phase 4 Sales Budget pattern.
// Tasks 5.7–5.13 all live here.

interface ResourceCostCentreBudgetViewProps {
  projectId: number;
  costCentres: CostCentre[];
  hierarchicalCostCentres: HierarchicalCostCentre[];
  isStructureView: boolean;
  projectStartDate?: string;
  projectEndDate?: string;
  resourceBudget: ResourceBudget;
  onChange: (changes: ResourceBudget) => void;
  onCellCommit?: () => void;
}

function ResourceCostCentreBudgetView({
  projectId,
  costCentres,
  hierarchicalCostCentres,
  isStructureView,
  projectStartDate,
  projectEndDate,
  resourceBudget,
  onChange,
  onCellCommit,
}: ResourceCostCentreBudgetViewProps) {
  const months = useMemo(
    () => buildProjectMonthsRb(projectStartDate, projectEndDate),
    [projectStartDate, projectEndDate],
  );

  // Derive all leaf/visible cost centres to initialise state
  const allCcs = useMemo<FlatCcRbNode[]>(() => {
    if (isStructureView) return flattenRbHierarchy(buildRbTree(hierarchicalCostCentres));
    return costCentres.map(cc => ({ ...cc, level: 0, hasChildren: false, parentId: null }));
  }, [isStructureView, costCentres, hierarchicalCostCentres]);

  // Track which parent rows are collapsed
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapse = (code: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const expandAllCcs = () => setCollapsed(new Set());

  const collapseAllCcs = () =>
    setCollapsed(new Set(allCcs.filter(cc => cc.hasChildren).map(cc => cc.code)));

  // Per-CC role rows: ccCode → RoleBudget[]
  // Initialise from existing resourceBudget.byRole costCentreCode assignments + localStorage
  const [ccRoles, setCcRoles] = useState<Record<string, RoleBudget[]>>(() => {
    const init: Record<string, RoleBudget[]> = {};
    for (const cc of allCcs) {
      if (!cc.hasChildren) {
        // Load saved state from localStorage first
        const saved = loadCcResourceBudget(projectId, cc.code);
        if (saved) {
          // Reconstruct roles from saved monthlyHours (already keyed by YYYY-MM)
          const existingRoles = (resourceBudget.byRole || []).filter(r => r.costCentreCode === cc.code);
          const existingRoleIds = new Set(existingRoles.map(r => String(r.id)));
          const rolesFromExisting = existingRoles.map(role => {
            const roleKey = String(role.id);
            const savedMonthly: Record<string, number> = saved.roleMonthlyHours[roleKey] ?? {};
            const totalHours = Object.values(savedMonthly).reduce((s, h) => s + h, 0);
            return {
              ...role,
              monthlyHours: savedMonthly,
              budgetHours: totalHours,
              budgetCost: totalHours * (role.costRate ?? role.billRate),
            };
          });

          // Also reconstruct any roles stored in localStorage that aren't in byRole
          let nextId = -1;
          const rolesFromStorage: RoleBudget[] = [];
          for (const [roleKey, monthlyMap] of Object.entries(saved.roleMonthlyHours)) {
            if (existingRoleIds.has(roleKey)) continue;
            const totalHours = Object.values(monthlyMap).reduce((s, h) => s + h, 0);
            if (totalHours <= 0) continue;
            const savedName = saved.roleNames?.[roleKey];
            const roleName = (savedName && savedName.trim()) ? savedName.trim() : `Role ${roleKey}`;
            const billRate = saved.roleRates?.[roleKey] ?? 150;
            rolesFromStorage.push({
              id: parseInt(roleKey, 10) || nextId--,
              role: roleName,
              budgetHours: totalHours,
              allocatedHours: totalHours,
              actualHours: 0,
              remainingHours: totalHours,
              percentUsed: 0,
              billRate,
              budgetCost: totalHours * billRate,
              actualCost: 0,
              monthlyHours: monthlyMap,
              costCentreCode: cc.code,
            });
          }

          init[cc.code] = [...rolesFromExisting, ...rolesFromStorage];
        } else {
          // Fall back to resourceBudget.byRole filtered by costCentreCode
          const roles = (resourceBudget.byRole || []).filter(r => r.costCentreCode === cc.code);
          init[cc.code] = roles.map(role => ({
            ...role,
            monthlyHours: normaliseMonthlyHours(role.monthlyHours, projectStartDate || new Date().toISOString()),
          }));
        }
      }
    }
    return init;
  });

  // Fetched roles for the Add Role picker
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchRoles()
      .then(roles => { if (!cancelled) setAvailableRoles(roles); })
      .catch(() => {
        if (!cancelled) {
          setAvailableRoles(
            ['Project Manager','Senior Consultant','Consultant','Developer','Senior Developer',
             'QA Analyst','Business Analyst','Technical Architect','UX Designer'].map((name, i) => ({
              id: i + 1, name, defaultCostRate: 0, defaultBillRate: 0,
            }))
          );
        }
      });
    return () => { cancelled = true; };
  }, []);

  const isInitialMount = useRef(true);

  // Helper to get bill rate for a role name (CC mode)
  const getRoleBillRate = (roleName: string, fallbackRate: number = 150): number => {
    const apiRole = availableRoles.find(r => r.name === roleName);
    if (apiRole?.defaultBillRate != null) return apiRole.defaultBillRate;
    return fallbackRate;
  };

  // Helper to get cost rate for a role name (CC mode)
  const getRoleCostRate = (roleName: string, fallbackBillRate: number): number => {
    const apiRole = availableRoles.find(r => r.name === roleName);
    if (apiRole?.defaultCostRate != null) return apiRole.defaultCostRate;
    return fallbackBillRate;
  };

  // ── Fill / Shift / Pattern state ──────────────────────────────────────────
  const [ccSelectedCell, setCcSelectedCell] = useState<{
    ccCode: string; roleIdx: number; monthIdx: number;
  } | null>(null);

  const [ccFillOpen, setCcFillOpen] = useState(false);
  const [ccFillSource, setCcFillSource] = useState<{
    ccCode: string; roleIdx: number; monthIdx: number; value: number;
  } | null>(null);

  const [ccShiftOpen, setCcShiftOpen] = useState(false);
  const [ccShiftSource, setCcShiftSource] = useState<{
    ccCode: string; roleIdx: number; roleName: string;
  } | null>(null);

  const [ccPatternSuggestion, setCcPatternSuggestion] = useState<{
    ccCode: string; roleIdx: number; value: number; fromMonth: number; toMonth: number;
  } | null>(null);
  const [ccDismissedSuggestions, setCcDismissedSuggestions] = useState<Set<string>>(new Set());

  // Propagate changes on every edit (API persist deferred to Save Budget)
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }

    // Update localStorage cache for immediate UI consistency on re-open
    const pendingCcBudgets: { costCentreId: number; budgetType: 'resource'; monthlyBudgets: Record<string, number> }[] = [];
    for (const [ccCode, roles] of Object.entries(ccRoles)) {
      const roleMonthlyHours: Record<string, Record<string, number>> = {};
      const roleNames: Record<string, string> = {};
      const roleRates: Record<string, number> = {};
      for (const role of roles) {
        const key = String(role.id);
        roleMonthlyHours[key] = role.monthlyHours || {};
        roleNames[key] = role.role;
        roleRates[key] = role.billRate;
      }
      saveCcResourceBudget(projectId, ccCode, { roleMonthlyHours, roleNames, roleRates });
      // Collect aggregated monthly hours for the save flow (no immediate API call)
      const cc = costCentres?.find(c => c.code === ccCode);
      if (cc) {
        const aggregatedMonthly: Record<string, number> = {};
        for (const monthlyMap of Object.values(roleMonthlyHours)) {
          for (const [month, hours] of Object.entries(monthlyMap)) {
            aggregatedMonthly[month] = (aggregatedMonthly[month] ?? 0) + hours;
          }
        }
        pendingCcBudgets.push({ costCentreId: cc.id, budgetType: 'resource', monthlyBudgets: aggregatedMonthly });
      }
    }

    // Build flat byRole for onChange callback (all CCs combined)
    const allRoles: RoleBudget[] = [];
    for (const [ccCode, roles] of Object.entries(ccRoles)) {
      for (const role of roles) {
        allRoles.push({ ...role, costCentreCode: ccCode });
      }
    }

    const totalBudget = allRoles.reduce((s, r) => s + r.budgetHours, 0);
    const totalBudgetCost = allRoles.reduce((s, r) => s + (r.budgetCost ?? 0), 0);
    const totalActual = allRoles.reduce((s, r) => s + (r.actualHours ?? 0), 0);

    onChange({
      ...resourceBudget,
      byRole: allRoles,
      summary: {
        ...resourceBudget.summary,
        totalBudget,
        totalBudgetCost,
        totalActual,
        totalRemaining: totalBudget - totalActual,
        progressPercent: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
      },
      pendingCcBudgets,
    });
  }, [ccRoles]);

  // Helper to update a single cell
  const updateHours = (ccCode: string, roleIdx: number, monthIdx: number, hours: number) => {
    const key = months[monthIdx];
    setCcRoles(prev => {
      const roles = [...(prev[ccCode] || [])];
      const role = { ...roles[roleIdx] };
      const monthlyHours = { ...(role.monthlyHours || {}) };
      monthlyHours[key] = hours;
      const totalHours = Object.values(monthlyHours).reduce((s, h) => s + h, 0);
      roles[roleIdx] = {
        ...role,
        monthlyHours,
        budgetHours: totalHours,
        remainingHours: totalHours - (role.actualHours ?? 0),
        percentUsed: totalHours > 0 ? ((role.actualHours ?? 0) / totalHours) * 100 : 0,
        budgetCost: totalHours * (role.costRate ?? role.billRate),
      };
      // Pattern detection
      const suggestionKey = `${ccCode}-${roleIdx}`;
      if (!ccDismissedSuggestions.has(suggestionKey) && monthIdx >= 2 && hours > 0) {
        const last3 = [monthlyHours[months[monthIdx - 2]] ?? 0, monthlyHours[months[monthIdx - 1]] ?? 0, monthlyHours[key] ?? 0];
        if (last3.every(v => v === hours) && monthIdx < months.length - 1) {
          setCcPatternSuggestion({ ccCode, roleIdx, value: hours, fromMonth: monthIdx + 1, toMonth: months.length - 1 });
        }
      }
      return { ...prev, [ccCode]: roles };
    });
  };

  // Fill handlers
  const handleCcFillClick = (ccCode: string, roleIdx: number, monthIdx: number) => {
    const role = ccRoles[ccCode]?.[roleIdx];
    if (!role) return;
    const value = role.monthlyHours?.[months[monthIdx]] ?? 0;
    setCcFillSource({ ccCode, roleIdx, monthIdx, value });
    setCcFillOpen(true);
  };

  const handleCcFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!ccFillSource) return;
    const { ccCode, roleIdx, monthIdx, value } = ccFillSource;
    setCcRoles(prev => {
      const roles = [...(prev[ccCode] || [])];
      const role = { ...roles[roleIdx] };
      const monthlyHours = { ...(role.monthlyHours || {}) };
      const startMonth = horizontalDirection === 'right' ? monthIdx : toMonth;
      const endMonth   = horizontalDirection === 'right' ? toMonth  : monthIdx;
      const count = Math.abs(endMonth - startMonth) + 1;
      if (fillType === 'copy') {
        for (let i = startMonth; i <= endMonth; i++) monthlyHours[months[i]] = value;
      } else {
        const per = Math.floor((value / count) * 100) / 100;
        const rem = value - per * count;
        for (let i = startMonth; i <= endMonth; i++) monthlyHours[months[i]] = i === endMonth ? per + rem : per;
      }
      const totalHours = Object.values(monthlyHours).reduce((s, h) => s + h, 0);
      roles[roleIdx] = { ...role, monthlyHours, budgetHours: totalHours, budgetCost: totalHours * (role.costRate ?? role.billRate) };
      return { ...prev, [ccCode]: roles };
    });
    setCcFillSource(null);
    onCellCommit?.();
  };

  // Shift handler
  const handleCcShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!ccShiftSource) return;
    const { ccCode, roleIdx } = ccShiftSource;
    setCcRoles(prev => {
      const roles = [...(prev[ccCode] || [])];
      const role = { ...roles[roleIdx] };
      const monthlyHours = { ...(role.monthlyHours || {}) };
      // Extract source values before mutation
      const chunk = Array.from({ length: sourceTo - sourceFrom + 1 }, (_, j) => monthlyHours[months[sourceFrom + j]] ?? 0);
      if (clearSource) for (let i = sourceFrom; i <= sourceTo; i++) monthlyHours[months[i]] = 0;
      const end = Math.min(destinationStart + chunk.length - 1, months.length - 1);
      for (let i = destinationStart; i <= end; i++) monthlyHours[months[i]] = chunk[i - destinationStart];
      const totalHours = Object.values(monthlyHours).reduce((s, h) => s + h, 0);
      roles[roleIdx] = { ...role, monthlyHours, budgetHours: totalHours, budgetCost: totalHours * (role.costRate ?? role.billRate) };
      return { ...prev, [ccCode]: roles };
    });
    onCellCommit?.();
  };

  // Pattern suggestion handlers
  const handleCcPatternAccept = () => {
    if (!ccPatternSuggestion) return;
    const { ccCode, roleIdx, value, fromMonth, toMonth } = ccPatternSuggestion;
    setCcRoles(prev => {
      const roles = [...(prev[ccCode] || [])];
      const role = { ...roles[roleIdx] };
      const monthlyHours = { ...(role.monthlyHours || {}) };
      for (let i = fromMonth; i <= toMonth; i++) monthlyHours[months[i]] = value;
      const totalHours = Object.values(monthlyHours).reduce((s, h) => s + h, 0);
      roles[roleIdx] = { ...role, monthlyHours, budgetHours: totalHours, budgetCost: totalHours * (role.costRate ?? role.billRate) };
      return { ...prev, [ccCode]: roles };
    });
    setCcPatternSuggestion(null);
    onCellCommit?.();
  };

  const handleCcPatternDismiss = () => {
    if (ccPatternSuggestion) {
      const key = `${ccPatternSuggestion.ccCode}-${ccPatternSuggestion.roleIdx}`;
      setCcDismissedSuggestions(prev => new Set(prev).add(key));
    }
    setCcPatternSuggestion(null);
  };

  // Add a role to a cost centre section
  const addRoleToCc = (ccCode: string) => {
    const firstRole = availableRoles[0];
    const defaultBillRate = firstRole ? getRoleBillRate(firstRole.name, 150) : 150;
    const defaultCostRate = firstRole ? getRoleCostRate(firstRole.name, defaultBillRate) : defaultBillRate;
    const newRole: RoleBudget = {
      id: -(Date.now() + Math.floor(Math.random() * 1000)), // stable negative IDs for new roles (Issue 3)
      roleId: firstRole?.id,
      role: firstRole?.name ?? 'Select Role',
      budgetHours: 0,
      allocatedHours: 0,
      actualHours: 0,
      remainingHours: 0,
      percentUsed: 0,
      billRate: defaultBillRate,
      costRate: defaultCostRate,
      budgetCost: 0,
      actualCost: 0,
      monthlyHours: {},
      costCentreCode: ccCode,
    };
    setCcRoles(prev => ({ ...prev, [ccCode]: [...(prev[ccCode] || []), newRole] }));
  };

  // Update role field (CC mode)
  const updateRoleField = (ccCode: string, roleIdx: number, field: keyof RoleBudget, value: unknown) => {
    setCcRoles(prev => {
      const roles = [...(prev[ccCode] || [])];
      roles[roleIdx] = { ...roles[roleIdx], [field]: value };
      // If role changed, update billRate and costRate
      if (field === 'role' && typeof value === 'string') {
        const newBillRate = getRoleBillRate(value, roles[roleIdx].billRate);
        const newCostRate = getRoleCostRate(value, newBillRate);
        roles[roleIdx].billRate = newBillRate;
        roles[roleIdx].costRate = newCostRate;
        const budgetHours = roles[roleIdx].budgetHours;
        roles[roleIdx].budgetCost = budgetHours * newCostRate;
        // Keep roleId in sync with the chosen role name so the saved
        // RoleBudget references the correct role (and the backend can resolve
        // the name from the Role table if the name column is ever blank).
        const matchedRole = availableRoles.find(r => r.name === value);
        if (matchedRole) roles[roleIdx].roleId = matchedRole.id;
      }
      return { ...prev, [ccCode]: roles };
    });
  };

  // Remove role from a cost centre section
  const removeRoleFromCc = (ccCode: string, roleIdx: number) => {
    setCcRoles(prev => {
      const roles = [...(prev[ccCode] || [])];
      roles.splice(roleIdx, 1);
      return { ...prev, [ccCode]: roles };
    });
  };

  // Compute auto-sum for a parent CC from its children
  const getParentTotals = (parentCode: string) => {
    // Find children in allCcs
    const parentNode = allCcs.find(cc => cc.code === parentCode);
    if (!parentNode) return { totalHours: 0 };

    // Collect all descendant codes
    const descendantCodes: string[] = [];
    const collect = (node: FlatCcRbNode) => {
      if (!node.hasChildren) {
        descendantCodes.push(node.code);
      } else {
        // Find children
        const children = allCcs.filter(cc => cc.parentId === node.id);
        children.forEach(c => collect(c as FlatCcRbNode));
      }
    };
    collect(parentNode);

    const totalHours = descendantCodes.reduce((sum, code) => {
      const roles = ccRoles[code] || [];
      return sum + roles.reduce((s, r) => s + r.budgetHours, 0);
    }, 0);

    return { totalHours };
  };

  // Monthly totals for a parent CC (auto-sum from children)
  const getParentMonthlyTotals = (parentCode: string): number[] => {
    const parentNode = allCcs.find(cc => cc.code === parentCode);
    if (!parentNode) return Array(months.length).fill(0);

    const totals = Array(months.length).fill(0);
    const descendantCodes: string[] = [];
    const collect = (node: FlatCcRbNode) => {
      if (!node.hasChildren) {
        descendantCodes.push(node.code);
      } else {
        const children = allCcs.filter(cc => cc.parentId === node.id);
        children.forEach(c => collect(c as FlatCcRbNode));
      }
    };
    collect(parentNode);

    for (const code of descendantCodes) {
      const roles = ccRoles[code] || [];
      for (const role of roles) {
        months.forEach((m, i) => { totals[i] = (totals[i] || 0) + (role.monthlyHours?.[m] ?? 0); });
      }
    }
    return totals;
  };

  // Grand total hours across all leaf cost centres
  const grandTotal = useMemo(() => {
    return Object.values(ccRoles).reduce((sum, roles) => sum + roles.reduce((s, r) => s + r.budgetHours, 0), 0);
  }, [ccRoles]);

  const grandMonthlyTotals = useMemo(() => {
    const totals = Array(months.length).fill(0);
    Object.values(ccRoles).forEach(roles => {
      roles.forEach(role => {
        months.forEach((m, i) => { totals[i] = (totals[i] || 0) + (role.monthlyHours?.[m] ?? 0); });
      });
    });
    return totals;
  }, [ccRoles, months.length]);

  if (allCcs.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center text-muted-foreground">
        <p>No cost centres available. Please add cost centres to this project first.</p>
      </div>
    );
  }

  // Determine which CC rows are visible (respecting parent collapse state)
  const visibleCcs = allCcs.filter(cc => {
    // Check if any ancestor is collapsed
    if (cc.parentId === null || cc.parentId === undefined) return true;
    // Find the parent
    const parent = allCcs.find(p => p.id === cc.parentId);
    if (!parent) return true;
    return !collapsed.has(parent.code);
  });

  // Build a flat list of all visible rows (CC header + role children) for synchronized rendering
  type CcRow =
    | { kind: 'cc'; cc: typeof visibleCcs[number]; roles: typeof ccRoles[string]; isParent: boolean }
    | { kind: 'role'; cc: typeof visibleCcs[number]; role: { id: number; role: string; budgetHours: number; monthlyHours?: Record<string, number>; billRate: number }; roleIdx: number };

  const rows: CcRow[] = [];
  for (const cc of visibleCcs) {
    const isParent = cc.hasChildren;
    const parent = allCcs.find(p => p.id === cc.parentId);
    if (cc.parentId !== null && cc.parentId !== undefined && parent && collapsed.has(parent.code)) continue;
    const roles = ccRoles[cc.code] || [];
    rows.push({ kind: 'cc', cc, roles, isParent });
    if (!isParent && !collapsed.has(cc.code)) {
      roles.forEach((role, roleIdx) => {
        rows.push({ kind: 'role', cc, role, roleIdx });
      });
    }
  }

  const ROW_H = 'h-10'; // consistent row height across all 3 sections

  // Derive selected cell role for toolbar button state
  const ccSelRole = ccSelectedCell
    ? ccRoles[ccSelectedCell.ccCode]?.[ccSelectedCell.roleIdx]
    : null;
  const ccSelHours = ccSelectedCell && ccSelRole
    ? (ccSelRole.monthlyHours?.[months[ccSelectedCell.monthIdx]] ?? 0)
    : 0;

  return (
    <div className="border rounded-none overflow-hidden">

      {/* ── Header — matches regular form's "Budget Hours by Role" header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Budget Hours by Cost Centre</h2>
          {isStructureView && (
            <>
              <Button size="sm" variant="outline" onClick={expandAllCcs}>
                Expand All
              </Button>
              <Button size="sm" variant="outline" onClick={collapseAllCcs}>
                Collapse All
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!ccSelectedCell || ccSelHours === 0 || months.length <= 1}
            title="Fill from selected cell across months"
            onClick={() => {
              if (ccSelectedCell) handleCcFillClick(ccSelectedCell.ccCode, ccSelectedCell.roleIdx, ccSelectedCell.monthIdx);
            }}
          >
            <ArrowLeftRight className="h-4 w-4 mr-2" />
            Fill
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!ccSelectedCell || ccSelHours === 0}
            title="Shift budget between months (select a monthly cell first)"
            onClick={() => {
              if (ccSelectedCell && ccSelRole) {
                setCcShiftSource({ ccCode: ccSelectedCell.ccCode, roleIdx: ccSelectedCell.roleIdx, roleName: ccSelRole.role });
                setCcShiftOpen(true);
              }
            }}
          >
            <MoveHorizontal className="h-4 w-4 mr-2" />
            Shift
          </Button>
        </div>
      </div>

      {/* 3-Column Layout: Left (fixed) + Middle (scrollable) + Right (fixed) */}
      <div className="flex">

        {/* ── LEFT: Code column + Name column (fixed, no scroll) ── */}
        <div className="flex-shrink-0 border-r">
          <Table className="border-collapse">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[100px] font-semibold border-r">Code</TableHead>
                <TableHead className="w-[220px] font-semibold">Name / Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                if (row.kind === 'cc') {
                  const { cc, isParent } = row;
                  const rowBg = isParent ? 'bg-muted/40' : 'bg-muted/10';
                  return (
                    <TableRow
                      key={`left-cc-${cc.code}`}
                      className={cn('group', rowBg)}
                    >
                      {/* Code cell */}
                      <TableCell className={cn('w-[100px] border-r py-2 px-2', rowBg)}>
                        <div className="flex items-center gap-1" style={{ paddingLeft: `${cc.level}rem` }}>
                          {isParent ? (
                            <button
                              type="button"
                              onClick={() => toggleCollapse(cc.code)}
                              className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                              aria-label={collapsed.has(cc.code) ? 'Expand' : 'Collapse'}
                            >
                              {collapsed.has(cc.code)
                                ? <ChevronRight className="h-4 w-4" />
                                : <ChevronDown className="h-4 w-4" />}
                            </button>
                          ) : (
                            <span className="w-4 flex-shrink-0" />
                          )}
                          <span className={cn('text-sm font-medium', isParent ? 'font-bold text-foreground' : 'text-muted-foreground')}>
                            {cc.code}
                          </span>
                        </div>
                      </TableCell>
                      {/* Name cell */}
                      <TableCell className={cn('w-[220px] py-2 px-2', rowBg)}>
                        <div className="flex items-center gap-1.5" style={{ paddingLeft: `${cc.level}rem` }}>
                          {isParent
                            ? collapsed.has(cc.code)
                              ? <Folder className="h-4 w-4 text-info-500 shrink-0" />
                              : <FolderOpen className="h-4 w-4 text-info-500 shrink-0" />
                            : null}
                          <span className={cn('truncate text-sm', isParent ? 'font-semibold text-foreground' : 'text-foreground/80')}>
                            {cc.name}
                          </span>
                          {!isParent && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 ml-auto flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent"
                              onClick={() => addRoleToCc(cc.code)}
                              title={`Add role to ${cc.code}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  const { cc, role, roleIdx } = row;
                  return (
                    <TableRow key={`left-role-${cc.code}-${role.id}`} className="hover:!bg-transparent">
                      {/* Empty code cell for role rows */}
                      <TableCell className="w-[100px] border-r py-1 px-2 bg-background" />
                      {/* Role combobox + remove button */}
                      <TableCell className="w-[220px] py-1 px-2 bg-background">
                        <div className="flex items-center gap-1" style={{ paddingLeft: `${cc.level + 1}rem` }}>
                          <div className="flex-1 min-w-0">
                            <Combobox
                              options={availableRoles.map(r => ({ value: r.name, label: r.name }))}
                              value={role.role}
                              onValueChange={val => updateRoleField(cc.code, roleIdx, 'role', val)}
                              placeholder="Select role"
                              searchPlaceholder="Search roles..."
                              emptyMessage="No role found."
                              className="h-8 text-sm w-full"
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeRoleFromCc(cc.code, roleIdx)}
                            title="Remove role"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="w-[100px] font-semibold bg-muted border-r" />
                <TableCell className="w-[220px] font-semibold bg-muted">Grand Total</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>

        {/* ── MIDDLE: Monthly columns (scrollable) ── */}
        <div
          className="flex-1 overflow-x-auto [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)' }}
        >
          <Table
            className="border-collapse"
            style={{ width: `${months.length * 100}px`, minWidth: '100%' }}
          >
            <TableHeader>
              <TableRow className="bg-muted/50">
                {months.map(m => {
                  const [year, month] = m.split('-');
                  return (
                    <TableHead key={m} className="w-[100px] text-center font-semibold">
                      {SHORT_MONTHS[parseInt(month, 10) - 1]}/{String(year).slice(-2)}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => {
                if (row.kind === 'cc') {
                  const { cc, roles, isParent } = row;
                  const parentMonthlyTotals = isParent ? getParentMonthlyTotals(cc.code) : null;
                  return (
                    <TableRow
                      key={`mid-cc-${cc.code}`}
                      className={isParent ? 'bg-muted/40' : 'bg-muted/10'}
                    >
                      {months.map((m, i) => {
                        const val = isParent
                          ? (parentMonthlyTotals?.[i] ?? 0)
                          : roles.reduce((s, r) => s + (r.monthlyHours?.[m] ?? 0), 0);
                        return (
                          <TableCell key={m} className="w-[100px] text-center text-sm text-muted-foreground font-medium">
                            {val > 0 ? val.toFixed(0) : '—'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                } else {
                  const { cc, role, roleIdx } = row;
                  return (
                    <TableRow key={`mid-role-${cc.code}-${role.id}`} className="hover:!bg-transparent">
                      {months.map((m, monthIdx) => {
                        const isSelected =
                          ccSelectedCell?.ccCode === cc.code &&
                          ccSelectedCell?.roleIdx === roleIdx &&
                          ccSelectedCell?.monthIdx === monthIdx;
                        const hours = role.monthlyHours?.[months[monthIdx]] ?? 0;
                        return (
                          <TableCell key={m} className="p-1 w-[100px]">
                            <div className="relative group/cell">
                              <Input
                                type="number"
                                min={0}
                                step={0.5}
                                value={hours}
                                onChange={e => updateHours(cc.code, roleIdx, monthIdx, parseFloat(e.target.value) || 0)}
                                onFocus={e => {
                                  e.target.select();
                                  setCcSelectedCell({ ccCode: cc.code, roleIdx, monthIdx });
                                }}
                                onBlur={() => onCellCommit?.()}
                                onClick={() => setCcSelectedCell({ ccCode: cc.code, roleIdx, monthIdx })}
                                className={cn(
                                  'h-8 w-full text-center text-sm px-1',
                                  isSelected && 'ring-2 ring-primary'
                                )}
                                placeholder="0"
                              />
                              {hours > 0 && monthIdx < months.length - 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 opacity-0 group-hover/cell:opacity-100 transition-opacity absolute -right-3 top-1/2 -translate-y-1/2 z-10"
                                  onClick={() => handleCcFillClick(cc.code, roleIdx, monthIdx)}
                                  title="Fill right"
                                  tabIndex={-1}
                                >
                                  <ArrowRight className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                }
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                {grandMonthlyTotals.map((h, i) => (
                  <TableCell key={months[i]} className="w-[100px] text-center font-semibold bg-muted">
                    {h > 0 ? h.toFixed(0) : '—'}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          </Table>
        </div>

        {/* ── RIGHT: Total Hrs column (fixed, no scroll) ── */}
        <div className="flex-shrink-0 border-l-2">
          <Table className="border-collapse">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[100px] text-center font-semibold">Total Hrs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => {
                if (row.kind === 'cc') {
                  const { cc, roles, isParent } = row;
                  const parentTotal = isParent ? getParentTotals(cc.code).totalHours : 0;
                  const leafTotal = roles.reduce((s, r) => s + r.budgetHours, 0);
                  const total = isParent ? parentTotal : leafTotal;
                  return (
                    <TableRow
                      key={`right-cc-${cc.code}`}
                      className={isParent ? 'bg-muted/40' : 'bg-muted/10'}
                    >
                      <TableCell className="w-[100px] h-9 text-center text-sm font-semibold">
                        {total > 0 ? `${total.toFixed(0)}h` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                } else {
                  const { cc, role } = row;
                  return (
                    <TableRow key={`right-role-${cc.code}-${role.id}`} className="hover:!bg-transparent">
                      <TableCell className="w-[100px] h-9 text-center text-sm font-semibold">
                        {role.budgetHours > 0 ? `${role.budgetHours.toFixed(0)}h` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                }
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="w-[100px] h-9 text-center text-sm font-bold">
                  {grandTotal > 0 ? `${grandTotal.toFixed(0)}h` : '—'}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>

      </div>

      {/* AI Pattern Suggestion */}
      {ccPatternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${ccPatternSuggestion.value.toFixed(2)} hours?`}
          details={`You've entered ${ccPatternSuggestion.value.toFixed(2)} hours for 3 consecutive months. Would you like to fill ${SHORT_MONTHS[parseInt(months[ccPatternSuggestion.fromMonth]?.split('-')[1] ?? '1', 10) - 1]} through ${SHORT_MONTHS[parseInt(months[ccPatternSuggestion.toMonth]?.split('-')[1] ?? '1', 10) - 1]} with the same value?`}
          confidence={95}
          onAccept={handleCcPatternAccept}
          onDismiss={handleCcPatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={ccFillOpen}
        onOpenChange={setCcFillOpen}
        sourceValue={ccFillSource?.value ?? 0}
        sourceMonth={ccFillSource?.monthIdx ?? 0}
        direction="right"
        onApply={handleCcFillApply}
        totalMonths={months.length}
        monthLabels={months.map(m => {
          const [year, month] = m.split('-');
          return `${SHORT_MONTHS[parseInt(month, 10) - 1]}/${String(year).slice(-2)}`;
        })}
        budgetType="resource"
      />

      {/* Shift Budget Sheet */}
      {ccShiftSource && (
        <ShiftBudgetSheet
          open={ccShiftOpen}
          onOpenChange={setCcShiftOpen}
          roleName={ccShiftSource.roleName}
          monthlyHours={months.map(m => ccRoles[ccShiftSource.ccCode]?.[ccShiftSource.roleIdx]?.monthlyHours?.[m] ?? 0)}
          selectedMonthIndex={ccSelectedCell?.monthIdx}
          onShift={handleCcShiftBudget}
          totalMonths={months.length}
          monthLabels={months.map(m => {
            const [year, month] = m.split('-');
            return `${SHORT_MONTHS[parseInt(month, 10) - 1]}/${String(year).slice(-2)}`;
          })}
          budgetType="resource"
        />
      )}

    </div>
  );
}

export function EditResourceBudgetForm({
  projectId,
  resourceBudget,
  onChange,
  onCellCommit,
  selectedYear,
  projectStartDate,
  projectEndDate,
  isCostCentreMode = false,
  costCentres = [],
  hierarchicalCostCentres = [],
  isStructureView = false,
}: EditResourceBudgetFormProps) {
  // ── Cost Centre mode — delegate to dedicated view ────────────────────────
  if (isCostCentreMode) {
    return (
      <ResourceCostCentreBudgetView
        projectId={projectId}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        projectStartDate={projectStartDate}
        projectEndDate={projectEndDate}
        resourceBudget={resourceBudget}
        onChange={onChange}
        onCellCommit={onCellCommit}
      />
    );
  }
  // Get rate calculations from Rate Card
  const { roleBreakdown } = useBudgetCalculations(String(projectId), resourceBudget);

  // Create maps of role names to their bill rates and cost rates from Rate Card + API roles
  const roleRateMap = useMemo(() => {
    const map = new Map<string, number>();
    roleBreakdown.forEach(role => {
      map.set(role.roleName, role.averageBillRate);
    });
    console.log('[EditResourceBudgetForm] Role rate map created:', Array.from(map.entries()));
    return map;
  }, [roleBreakdown]);

  // NOTE: roleCostRateMap and rate helpers are defined after availableRoles state below

  // Calculate all available months for the project across all years
  const allProjectMonths = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Show all months of current year if no project dates
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => ({ year: currentYear, month: i }));
    }
    
    const startDate = new Date(projectStartDate);
    const endDate = new Date(projectEndDate);
    
    const months: { year: number; month: number }[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      months.push({ year: current.getFullYear(), month: current.getMonth() });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  })();

  // Show ALL months for the entire project duration - no year filtering
  const yearMonths = allProjectMonths;

  // Debug logging
  console.log('[EditResourceBudgetForm] Project dates:', { projectStartDate, projectEndDate });
  console.log('[EditResourceBudgetForm] Total months to display:', yearMonths.length);
  console.log('[EditResourceBudgetForm] Month range:', yearMonths.length > 0 ? `${MONTHS[yearMonths[0].month]} ${yearMonths[0].year} - ${MONTHS[yearMonths[yearMonths.length - 1].month]} ${yearMonths[yearMonths.length - 1].year}` : 'None');

  // Create a mapping from global month index to year/month
  // This allows us to work with a flat array while preserving year information
  const monthIndexMap = yearMonths.map((ym, index) => ({
    globalIndex: index,
    year: ym.year,
    month: ym.month,
    yearMonthKey: `${ym.year}-${ym.month}` // Unique key for this month
  }));

  // YYYY-MM keys for keyed monthlyHours dict
  const monthKeys = yearMonths.map(ym => `${ym.year}-${String(ym.month + 1).padStart(2, '0')}`);

  // Show ALL months - no dynamic loading
  const visibleMonths = monthIndexMap.map(m => m.globalIndex);
  
  // Total number of months to display
  const totalMonths = yearMonths.length;

  // No dynamic loading needed - all months are rendered from the start
  
  // Initialize byRole with keyed monthlyHours (normalise legacy arrays on load)
  const [byRole, setByRole] = useState<RoleBudget[]>(() => {
    const roles = resourceBudget?.byRole || [];
    
    return roles.map(role => ({
      ...role,
      monthlyHours: normaliseMonthlyHours(role.monthlyHours, projectStartDate || new Date().toISOString()),
    }));
  });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; role: RoleBudget; rolesWithHours?: RoleBudget[] } | null>(null);
  const [byPerson, setByPerson] = useState<PersonBudget[]>(resourceBudget?.byPerson || []);
  const [byPhase, setByPhase] = useState<PhaseBudgetResource[]>(resourceBudget?.byPhase || []);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  
  // Fill dialog state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ roleIndex: number; monthIndex: number; value: number } | null>(null);
  
  // AI suggestion state
  const [patternSuggestion, setPatternSuggestion] = useState<{
    roleIndex: number;
    value: number;
    fromMonth: number;
    toMonth: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Clipboard state for copy/paste
  const [clipboard, setClipboard] = useState<{
    data: number | Record<string, number> | null;
    type: 'cell' | 'row' | null;
    sourceRoleIndex: number | null;
    sourceMonthIndex: number | null;
    isCut: boolean;
  }>({ data: null, type: null, sourceRoleIndex: null, sourceMonthIndex: null, isCut: false });

  // Selection state - can be a single cell or entire row
  const [selectedCell, setSelectedCell] = useState<{
    roleIndex: number;
    monthIndex: number | null; // null means entire row selected
  } | null>(null);

  // Shift budget dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ roleIndex: number; roleName: string } | null>(null);

  // Fetched roles from API
  const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
  const roleOptionNames = useMemo(() => availableRoles.map(r => r.name), [availableRoles]);

  const roleCostRateMap = useMemo(() => {
    const map = new Map<string, number>();
    roleBreakdown.forEach(role => {
      map.set(role.roleName, role.averageCostRate);
    });
    // Also populate from fetched API roles (defaultCostRate) as fallback
    availableRoles.forEach(role => {
      if (!map.has(role.name) && role.defaultCostRate != null) {
        map.set(role.name, role.defaultCostRate);
      }
    });
    return map;
  }, [roleBreakdown, availableRoles]);

  // Helper function to get bill rate from Rate Card
  const getRoleBillRate = (roleName: string, fallbackRate: number = 150): number => {
    const rateFromCard = roleRateMap.get(roleName);
    if (rateFromCard !== undefined) {
      return rateFromCard;
    }
    // Try fetched API roles
    const apiRole = availableRoles.find(r => r.name === roleName);
    if (apiRole?.defaultBillRate != null) {
      return apiRole.defaultBillRate;
    }
    console.warn('[EditResourceBudgetForm] No rate found for role:', roleName, 'using fallback:', fallbackRate);
    return fallbackRate;
  };

  // Helper function to get cost rate from Rate Card or API roles
  const getRoleCostRate = (roleName: string, fallbackBillRate: number): number => {
    const rateFromCard = roleCostRateMap.get(roleName);
    if (rateFromCard !== undefined) {
      return rateFromCard;
    }
    const apiRole = availableRoles.find(r => r.name === roleName);
    if (apiRole?.defaultCostRate != null) {
      return apiRole.defaultCostRate;
    }
    // Fall back to bill rate if no cost rate available
    return fallbackBillRate;
  };

  useEffect(() => {
    let cancelled = false;
    fetchRoles()
      .then((roles) => {
        if (!cancelled) {
          setAvailableRoles(roles);
        }
      })
      .catch((err) => {
        console.error('[EditResourceBudgetForm] Failed to fetch roles:', err);
        if (!cancelled) {
          setAvailableRoles(FALLBACK_ROLE_OPTIONS.map((name, i) => ({
            id: i + 1,
            name,
            defaultCostRate: 0,
            defaultBillRate: 0,
          })));
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Debug: Log when selectedCell changes
  useEffect(() => {
    console.log('selectedCell changed:', selectedCell);
  }, [selectedCell]);

  // Drag and drop removed - reordering disabled
  // const sensors = useSensors(
  //   useSensor(PointerSensor, {
  //     activationConstraint: {
  //       distance: 8,
  //     },
  //   })
  // );

  // Track if this is the initial mount to prevent marking form as dirty on load
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip onChange on initial mount to prevent false "unsaved changes" alert
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const summary = calculateSummary(byRole, byPerson);
    onChange({
      ...resourceBudget,
      byRole,
      byPerson,
      byPhase,
      summary,
    });
  }, [byRole, byPerson, byPhase]);

  const calculateSummary = (roles: RoleBudget[], people: PersonBudget[]) => {
    const totalBudget = roles.reduce((sum, role) => sum + (role.budgetHours || 0), 0);
    const totalActual = roles.reduce((sum, role) => sum + (role.actualHours || 0), 0);
    const totalRemaining = totalBudget - totalActual;
    const progressPercent = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    const totalBudgetCost = roles.reduce((sum, role) => sum + (role.budgetCost || 0), 0);
    const totalActualCost = roles.reduce((sum, role) => sum + (role.actualCost || 0), 0);

    // Calculate billable percentage (assuming all hours are billable for now)
    const billablePercent = totalActual > 0 ? 85 : 0; // Default assumption

    return {
      totalBudget,
      totalActual,
      totalRemaining,
      progressPercent,
      billablePercent,
      targetBillablePercent: 80,
      totalBudgetCost,
      totalActualCost,
    };
  };

  // Role handlers
  const addRole = () => {
    // Get roles already used in this project
    const usedRoles = byRole.map(r => r.role);
    
    // Find the first available role that's not already used
    const firstAvailable = availableRoles.find(r => !usedRoles.includes(r.name));
    
    // If all roles are used, default to empty string (user must select)
    const defaultRole = firstAvailable?.name || '';
    const defaultRoleId = firstAvailable?.id;
    const defaultBillRate = defaultRole ? getRoleBillRate(defaultRole, 150) : 150;
    const defaultCostRate = defaultRole ? getRoleCostRate(defaultRole, defaultBillRate) : defaultBillRate;
    
    const newRole: RoleBudget = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      roleId: defaultRoleId,
      role: defaultRole,
      budgetHours: 0,
      allocatedHours: 0,
      actualHours: 0,
      remainingHours: 0,
      percentUsed: 0,
      billRate: defaultBillRate,
      costRate: defaultCostRate,
      budgetCost: 0,
      actualCost: 0,
      monthlyHours: {},
    };
    setByRole([...byRole, newRole]);
  };

  const updateRole = (index: number, field: keyof RoleBudget, value: any) => {
    // Prevent modification of billRate field - it should be read-only
    if (field === 'billRate') {
      console.warn('billRate field is read-only and cannot be modified');
      return;
    }
    
    const updated = [...byRole];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // If role changed, update the billRate and costRate from Rate Card and set roleId
    if (field === 'role') {
      const matchedRole = availableRoles.find(r => r.name === value);
      if (matchedRole) {
        updated[index].roleId = matchedRole.id;
      }
      const newBillRate = getRoleBillRate(value, updated[index].billRate);
      const newCostRate = getRoleCostRate(value, newBillRate);
      updated[index].billRate = newBillRate;
      updated[index].costRate = newCostRate;
      
      // Recalculate costs with new cost rate
      const budgetHours = updated[index].budgetHours;
      const actualHours = updated[index].actualHours;
      updated[index].budgetCost = budgetHours * newCostRate;
      updated[index].actualCost = actualHours * newCostRate;
    }
    
    // Recalculate derived fields
    if (field === 'budgetHours' || field === 'actualHours') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      const effectiveCostRate = updated[index].costRate ?? updated[index].billRate;
      
      updated[index].remainingHours = budgetHours - actualHours;
      updated[index].percentUsed = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
      updated[index].budgetCost = budgetHours * effectiveCostRate;
      updated[index].actualCost = actualHours * effectiveCostRate;
    }
    
    setByRole(updated);
  };

  const updateMonthlyHours = (roleIndex: number, monthIndex: number, hours: number) => {
    const key = monthKeys[monthIndex];
    const updated = [...byRole];
    const monthlyHours = { ...(updated[roleIndex].monthlyHours || {}) };
    monthlyHours[key] = hours;
    
    // Calculate total from monthly hours
    const totalHours = Object.values(monthlyHours).reduce((sum, h) => sum + h, 0);
    
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours,
      budgetHours: totalHours,
    };
    
    // Recalculate derived fields
    const effectiveCostRate = updated[roleIndex].costRate ?? updated[roleIndex].billRate;
    const actualHours = updated[roleIndex].actualHours;
    updated[roleIndex].remainingHours = totalHours - actualHours;
    updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
    updated[roleIndex].budgetCost = totalHours * effectiveCostRate;
    updated[roleIndex].actualCost = actualHours * effectiveCostRate;
    
    setByRole(updated);
    
    // Pattern detection for AI suggestions
    detectPattern(roleIndex, monthIndex, hours, monthlyHours);
  };
  
  // Pattern detection for AI suggestions
  const detectPattern = (roleIndex: number, monthIndex: number, value: number, monthlyHours: Record<string, number>) => {
    // Don't suggest if already dismissed
    const suggestionKey = `pattern-${roleIndex}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    
    // Check if last 3 values are the same and non-zero
    if (monthIndex >= 2 && value > 0) {
      const last3 = [
        monthlyHours[monthKeys[monthIndex - 2]] ?? 0,
        monthlyHours[monthKeys[monthIndex - 1]] ?? 0,
        monthlyHours[monthKeys[monthIndex]] ?? 0,
      ];
      
      const allSame = last3.every(v => v === value);
      
      if (allSame && monthIndex < totalMonths - 1) {
        // Show suggestion to fill remaining months
        setPatternSuggestion({
          roleIndex,
          value,
          fromMonth: monthIndex + 1,
          toMonth: totalMonths - 1
        });
      }
    }
  };
  
  // Fill handlers
  const handleFillClick = (roleIndex: number, monthIndex: number) => {
    const role = byRole[roleIndex];
    const value = role.monthlyHours?.[monthKeys[monthIndex]] || 0;
    
    setFillSource({ roleIndex, monthIndex, value });
    setFillDialogOpen(true);
  };
  
  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;
    
    const { roleIndex, monthIndex, value } = fillSource;
    const updated = [...byRole];
    const monthlyHours = { ...(updated[roleIndex].monthlyHours || {}) };
    
    // Determine the range based on fill direction
    const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
    const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
    const monthCount = Math.abs(endMonth - startMonth) + 1;
    
    if (fillType === 'copy') {
      // Copy same value to each month
      for (let i = startMonth; i <= endMonth; i++) {
        monthlyHours[monthKeys[i]] = value;
      }
    } else {
      // Distribute equally
      const valuePerMonth = value / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100;
      const remainder = value - (roundedValue * monthCount);
      
      for (let i = startMonth; i <= endMonth; i++) {
        const isLast = i === endMonth;
        monthlyHours[monthKeys[i]] = isLast ? roundedValue + remainder : roundedValue;
      }
    }
    
    // Calculate total
    const totalHours = Object.values(monthlyHours).reduce((sum, h) => sum + h, 0);
    
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours,
      budgetHours: totalHours,
    };
    
    // Recalculate derived fields
    const effectiveCostRate = updated[roleIndex].costRate ?? updated[roleIndex].billRate;
    const actualHours = updated[roleIndex].actualHours;
    updated[roleIndex].remainingHours = totalHours - actualHours;
    updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
    updated[roleIndex].budgetCost = totalHours * effectiveCostRate;
    updated[roleIndex].actualCost = actualHours * effectiveCostRate;
    
    setByRole(updated);
    setFillSource(null);
    onCellCommit?.();
  };
  
  // Handle pattern suggestion acceptance
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    
    const { roleIndex, value, fromMonth, toMonth } = patternSuggestion;
    const updated = [...byRole];
    const monthlyHours = { ...(updated[roleIndex].monthlyHours || {}) };
    
    // Fill remaining months with the pattern value
    for (let i = fromMonth; i <= toMonth; i++) {
      monthlyHours[monthKeys[i]] = value;
    }
    
    // Calculate total
    const totalHours = Object.values(monthlyHours).reduce((sum, h) => sum + h, 0);
    
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours,
      budgetHours: totalHours,
    };
    
    // Recalculate derived fields
    const effectiveCostRate = updated[roleIndex].costRate ?? updated[roleIndex].billRate;
    const actualHours = updated[roleIndex].actualHours;
    updated[roleIndex].remainingHours = totalHours - actualHours;
    updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
    updated[roleIndex].budgetCost = totalHours * effectiveCostRate;
    updated[roleIndex].actualCost = actualHours * effectiveCostRate;
    
    setByRole(updated);
    setPatternSuggestion(null);
    onCellCommit?.();
  };
  
  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      const suggestionKey = `pattern-${patternSuggestion.roleIndex}`;
      setDismissedSuggestions(new Set(dismissedSuggestions).add(suggestionKey));
    }
    setPatternSuggestion(null);
  };

  // Copy/Paste handlers
  // Copy handler - copies entire row based on checkbox selection
  const handleCopy = () => {
    // Cell-level copy (single month)
    if (selectedCell && selectedCell.monthIndex !== null) {
      const role = byRole[selectedCell.roleIndex];
      if (!role) return;

      setClipboard({
        data: role.monthlyHours?.[monthKeys[selectedCell.monthIndex]] ?? 0,
        type: 'cell',
        sourceRoleIndex: selectedCell.roleIndex,
        sourceMonthIndex: selectedCell.monthIndex,
        isCut: false,
      });
      return;
    }

    // Row-level copy (all months) via checkbox
    if (selectedRows.size === 1) {
      const selectedRoleId = Array.from(selectedRows)[0];
      const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
      if (roleIndex === -1) return;
      
      const role = byRole[roleIndex];
      setClipboard({
        data: { ...(role.monthlyHours || {}) },
        type: 'row',
        sourceRoleIndex: roleIndex,
        sourceMonthIndex: null,
        isCut: false,
      });
    }
  };

  // Paste handler - supports both cell and row paste
  const handlePaste = () => {
    if (!clipboard.data) return;

    // Paste to cell (single month)
    if (clipboard.type === 'cell' && selectedCell && selectedCell.monthIndex !== null) {
      const role = byRole[selectedCell.roleIndex];
      if (!role) return;

      const updated = [...byRole];
      const monthlyHours = { ...(role.monthlyHours || {}) };
      monthlyHours[monthKeys[selectedCell.monthIndex]] = clipboard.data as number;
      const totalHours = Object.values(monthlyHours).reduce((sum, h) => sum + h, 0);

      updated[selectedCell.roleIndex] = {
        ...updated[selectedCell.roleIndex],
        monthlyHours,
        budgetHours: totalHours,
      };

      // Recalculate derived fields
      const effectiveCostRate = updated[selectedCell.roleIndex].costRate ?? updated[selectedCell.roleIndex].billRate;
      const actualHours = updated[selectedCell.roleIndex].actualHours;
      updated[selectedCell.roleIndex].remainingHours = totalHours - actualHours;
      updated[selectedCell.roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
      updated[selectedCell.roleIndex].budgetCost = totalHours * effectiveCostRate;
      updated[selectedCell.roleIndex].actualCost = actualHours * effectiveCostRate;

      setByRole(updated);

      // If it was a cut operation, clear the source cell
      if (clipboard.isCut && clipboard.sourceRoleIndex !== null && clipboard.sourceMonthIndex !== null) {
        const clearedUpdated = [...updated];
        const sourceMonthlyHours = { ...(clearedUpdated[clipboard.sourceRoleIndex].monthlyHours || {}) };
        sourceMonthlyHours[monthKeys[clipboard.sourceMonthIndex]] = 0;
        const sourceTotalHours = Object.values(sourceMonthlyHours).reduce((sum, h) => sum + h, 0);
        
        clearedUpdated[clipboard.sourceRoleIndex] = {
          ...clearedUpdated[clipboard.sourceRoleIndex],
          monthlyHours: sourceMonthlyHours,
          budgetHours: sourceTotalHours,
        };
        
        // Recalculate source derived fields
        const sourceCostRate = clearedUpdated[clipboard.sourceRoleIndex].costRate ?? clearedUpdated[clipboard.sourceRoleIndex].billRate;
        const sourceActualHours = clearedUpdated[clipboard.sourceRoleIndex].actualHours;
        clearedUpdated[clipboard.sourceRoleIndex].remainingHours = sourceTotalHours - sourceActualHours;
        clearedUpdated[clipboard.sourceRoleIndex].percentUsed = sourceTotalHours > 0 ? (sourceActualHours / sourceTotalHours) * 100 : 0;
        clearedUpdated[clipboard.sourceRoleIndex].budgetCost = sourceTotalHours * sourceCostRate;
        
        setByRole(clearedUpdated);
        setClipboard({ data: null, type: null, sourceRoleIndex: null, sourceMonthIndex: null, isCut: false });
      }
      return;
    }

    // Paste to row (all months) via checkbox
    if (clipboard.type === 'row' && selectedRows.size === 1) {
      const selectedRoleId = Array.from(selectedRows)[0];
      const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
      if (roleIndex === -1) return;
      
      const updated = [...byRole];
      const monthlyHours = { ...(clipboard.data as Record<string, number>) };
      const totalHours = Object.values(monthlyHours).reduce((sum, h) => sum + h, 0);
      
      updated[roleIndex] = {
        ...updated[roleIndex],
        monthlyHours,
        budgetHours: totalHours,
      };
      
      // Recalculate derived fields
      const effectiveCostRate = updated[roleIndex].costRate ?? updated[roleIndex].billRate;
      const actualHours = updated[roleIndex].actualHours;
      updated[roleIndex].remainingHours = totalHours - actualHours;
      updated[roleIndex].percentUsed = totalHours > 0 ? (actualHours / totalHours) * 100 : 0;
      updated[roleIndex].budgetCost = totalHours * effectiveCostRate;
      updated[roleIndex].actualCost = actualHours * effectiveCostRate;
      
      setByRole(updated);
      
      // If it was a cut operation, clear the source
      if (clipboard.isCut && clipboard.sourceRoleIndex !== null) {
        const clearedUpdated = [...updated];
        clearedUpdated[clipboard.sourceRoleIndex] = {
          ...clearedUpdated[clipboard.sourceRoleIndex],
          monthlyHours: {},
          budgetHours: 0,
          remainingHours: -clearedUpdated[clipboard.sourceRoleIndex].actualHours,
          percentUsed: 0,
          budgetCost: 0,
        };
        setByRole(clearedUpdated);
        setClipboard({ data: null, type: null, sourceRoleIndex: null, sourceMonthIndex: null, isCut: false });
      }
    }
  };

  const handleDuplicateRole = (roleIndex: number) => {
    const sourceRole = byRole[roleIndex];
    const newRole: RoleBudget = {
      ...sourceRole,
      id: Date.now() + Math.floor(Math.random() * 1000), // Generate unique numeric ID
      role: sourceRole.role, // Will be changed by user
      monthlyHours: { ...(sourceRole.monthlyHours || {}) },
    };
    
    // Insert below source role
    const updated = [
      ...byRole.slice(0, roleIndex + 1),
      newRole,
      ...byRole.slice(roleIndex + 1),
    ];
    
    setByRole(updated);
  };

  const handleDuplicateMultipleRoles = () => {
    if (selectedRows.size === 0) return;

    // Get all selected role IDs in their current order
    const selectedRoleIds = Array.from(selectedRows);
    
    // Create duplicates for each selected role
    const newRoles: RoleBudget[] = [];
    const updatedByRole = [...byRole];
    
    // Process in reverse order to maintain correct insertion positions
    for (let i = updatedByRole.length - 1; i >= 0; i--) {
      const role = updatedByRole[i];
      if (selectedRoleIds.includes(role.id)) {
        const newRole: RoleBudget = {
          ...role,
          id: Date.now() + Math.floor(Math.random() * 1000) + i, // Generate unique numeric ID
          role: role.role, // Will be changed by user
          monthlyHours: { ...(role.monthlyHours || {}) },
        };
        // Insert the duplicate right after the original
        updatedByRole.splice(i + 1, 0, newRole);
        newRoles.push(newRole);
      }
    }
    
    setByRole(updatedByRole);
    
    // Clear selection after duplication
    setSelectedRows(new Set());
  };

  // Cut handler - supports both cell and row cut
  const handleCut = () => {
    // Cell-level cut (single month)
    if (selectedCell && selectedCell.monthIndex !== null) {
      const role = byRole[selectedCell.roleIndex];
      if (!role) return;

      setClipboard({
        data: role.monthlyHours?.[monthKeys[selectedCell.monthIndex]] ?? 0,
        type: 'cell',
        sourceRoleIndex: selectedCell.roleIndex,
        sourceMonthIndex: selectedCell.monthIndex,
        isCut: true,
      });
      return;
    }

    // Row-level cut via checkbox
    if (selectedRows.size === 1) {
      const selectedRoleId = Array.from(selectedRows)[0];
      const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
      if (roleIndex === -1) return;
      
      const monthlyHours = { ...(byRole[roleIndex].monthlyHours || {}) };
      setClipboard({
        data: monthlyHours,
        type: 'row',
        sourceRoleIndex: roleIndex,
        sourceMonthIndex: null,
        isCut: true,
      });
    }
  };

  // Shift budget handler
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;
    
    const roleIndex = shiftSource.roleIndex;
    const role = byRole[roleIndex];
    const newMonthlyHours = { ...(role.monthlyHours || {}) };
    
    // Copy values to destination
    const shiftAmount = destinationStart - sourceFrom;
    // Extract source values before mutation
    const sourceValues = Array.from({ length: sourceTo - sourceFrom + 1 }, (_, j) => role.monthlyHours?.[monthKeys[sourceFrom + j]] ?? 0);
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        newMonthlyHours[monthKeys[destIndex]] = sourceValues[i - sourceFrom];
      }
    }
    
    // Clear source if requested - but only cells that are NOT in the destination range
    // This handles the case where source and destination ranges overlap
    if (clearSource) {
      const destinationEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        // Only clear if this cell is NOT in the destination range
        if (i < destinationStart || i > destinationEnd) {
          newMonthlyHours[monthKeys[i]] = 0;
        }
      }
    }
    
    // Update role
    const totalHours = Object.values(newMonthlyHours).reduce((sum, h) => sum + h, 0);
    const updated = [...byRole];
    updated[roleIndex] = {
      ...updated[roleIndex],
      monthlyHours: newMonthlyHours,
      budgetHours: totalHours,
      remainingHours: totalHours - updated[roleIndex].actualHours,
      percentUsed: totalHours > 0 ? (updated[roleIndex].actualHours / totalHours) * 100 : 0,
      budgetCost: totalHours * (updated[roleIndex].costRate ?? updated[roleIndex].billRate),
    };
    
    setByRole(updated);
    onCellCommit?.();
  };

  // Row selection handlers
  const toggleRowSelection = (roleId: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(roleId)) {
      newSelection.delete(roleId);
    } else {
      newSelection.add(roleId);
    }
    setSelectedRows(newSelection);
    // Clear cell selection when selecting a row
    setSelectedCell(null);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === byRole.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(byRole.map(role => role.id)));
    }
    // Clear cell selection when selecting all rows
    setSelectedCell(null);
  };

  // Drag and drop handler removed - reordering disabled
  // const handleDragEnd = (event: DragEndEvent) => {
  //   const { active, over } = event;
  //   
  //   if (!over || active.id === over.id) return;
  //   
  //   const oldIndex = byRole.findIndex((role) => role.id === active.id);
  //   const newIndex = byRole.findIndex((role) => role.id === over.id);
  //   
  //   if (oldIndex !== -1 && newIndex !== -1) {
  //     setByRole(arrayMove(byRole, oldIndex, newIndex));
  //   }
  // };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input field
      if (e.target instanceof HTMLInputElement) return;
      
      // Ctrl+C - Copy cell or row
      if (e.ctrlKey && e.key === 'c' && (selectedCell || selectedRows.size === 1)) {
        e.preventDefault();
        handleCopy();
      }
      
      // Ctrl+V - Paste cell or row
      if (e.ctrlKey && e.key === 'v' && (selectedCell || selectedRows.size === 1) && clipboard.data) {
        e.preventDefault();
        handlePaste();
      }
      
      // Ctrl+X - Cut cell or row
      if (e.ctrlKey && e.key === 'x' && (selectedCell || selectedRows.size === 1)) {
        e.preventDefault();
        handleCut();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRows, selectedCell, clipboard, byRole]);

  const removeRole = (index: number) => {
    const role = byRole[index];
    
    // Validation: Check if role has logged hours
    if (role.actualHours > 0) {
      // Show dialog only, no validation error alert
      setDeleteAttempt({ index, role });
      return;
    }
    
    // If no logged hours, allow removal
    setByRole(byRole.filter((_, i) => i !== index));
    setValidationError(null);
    
    // Clear selection state to prevent stale references to deleted role
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const removeMultipleRoles = () => {
    if (selectedRows.size === 0) return;

    // Get all selected role indices
    const selectedRoleIds = Array.from(selectedRows);
    const rolesToDelete = byRole.filter(role => selectedRoleIds.includes(role.id));
    
    // Check if any selected roles have logged hours
    const rolesWithHours = rolesToDelete.filter(role => role.actualHours > 0);
    
    if (rolesWithHours.length > 0) {
      // Show validation error for roles with logged hours
      // Pass all roles with hours to the dialog
      setDeleteAttempt({ index: -1, role: rolesWithHours[0], rolesWithHours });
      return;
    }
    
    // If no logged hours on any selected roles, allow removal
    setByRole(byRole.filter(role => !selectedRoleIds.includes(role.id)));
    setValidationError(null);
    
    // Clear selection state
    setSelectedRows(new Set());
    setSelectedCell(null);
  };
  
  const confirmDeleteRole = () => {
    // This should never be called if validation passes, but included for safety
    if (deleteAttempt && deleteAttempt.role.actualHours === 0) {
      setByRole(byRole.filter((_, i) => i !== deleteAttempt.index));
    }
    setDeleteAttempt(null);
    setValidationError(null);
  };
  
  const cancelDeleteRole = () => {
    setDeleteAttempt(null);
    setValidationError(null);
  };

  // Person handlers
  const addPerson = () => {
    const newPerson: PersonBudget = {
      id: -Date.now(),
      personId: -Date.now(),
      personName: 'Unassigned',
      role: 'Select Role',
      budgetHours: 0,
      actualHours: 0,
      utilisation: 0,
      costRate: 0,
      budgetCost: 0,
      actualCost: 0,
    };
    setByPerson([...byPerson, newPerson]);
  };

  const updatePerson = (index: number, field: keyof PersonBudget, value: any) => {
    const updated = [...byPerson];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate derived fields
    if (field === 'budgetHours' || field === 'actualHours' || field === 'costRate') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      const costRate = field === 'costRate' ? value : updated[index].costRate;
      
      updated[index].utilisation = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
      updated[index].budgetCost = budgetHours * costRate;
      updated[index].actualCost = actualHours * costRate;
    }
    
    setByPerson(updated);
  };

  const removePerson = (index: number) => {
    setByPerson(byPerson.filter((_, i) => i !== index));
  };

  // Phase handlers
  const addPhase = () => {
    const newPhase: PhaseBudgetResource = {
      phaseId: -Date.now(),
      phaseName: 'New Phase',
      budgetHours: 0,
      actualHours: 0,
      remainingHours: 0,
      status: 'not-started',
    };
    setByPhase([...byPhase, newPhase]);
  };

  const updatePhase = (index: number, field: keyof PhaseBudgetResource, value: any) => {
    const updated = [...byPhase];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate remaining hours
    if (field === 'budgetHours' || field === 'actualHours') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      updated[index].remainingHours = Math.max(0, budgetHours - actualHours);
    }
    
    setByPhase(updated);
  };

  const removePhase = (index: number) => {
    setByPhase(byPhase.filter((_, i) => i !== index));
  };

  const summary = calculateSummary(byRole, byPerson);

  return (
    <div>
      {/* Validation Error Alert */}
      {validationError && (
        <div className="px-4 pt-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Cannot Remove Role</AlertTitle>
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteRole()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Remove Role with Logged Hours</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {deleteAttempt?.rolesWithHours && deleteAttempt.rolesWithHours.length > 1 ? (
                  <>
                    The {deleteAttempt.rolesWithHours.length === 2 ? 'roles' : 'roles'}{' '}
                    {deleteAttempt.rolesWithHours.map((r, i) => (
                      <span key={r.id}>
                        <strong>"{r.role}"</strong>
                        {i < deleteAttempt.rolesWithHours!.length - 2 && ', '}
                        {i === deleteAttempt.rolesWithHours!.length - 2 && ' and '}
                      </span>
                    ))}{' '}
                    cannot be removed because {deleteAttempt.rolesWithHours.length === 2 ? 'they have' : 'they have'} logged hours associated with {deleteAttempt.rolesWithHours.length === 2 ? 'them' : 'them'}.
                  </>
                ) : (
                  <>
                    The role <strong>"{deleteAttempt?.role.role}"</strong> cannot be removed because it has logged hours associated with it.
                  </>
                )}
              </p>
              {/* Only show hours/cost details for single role */}
              {(!deleteAttempt?.rolesWithHours || deleteAttempt.rolesWithHours.length === 1) && (
                <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Logged Hours:</span>
                    <span className="font-semibold">{deleteAttempt && formatHours(deleteAttempt.role.actualHours)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Actual Cost:</span>
                    <span className="font-semibold">{deleteAttempt && formatCurrency(deleteAttempt.role.actualCost)}</span>
                  </div>
                </div>
              )}
              <p className="text-sm">
                <strong>To remove {deleteAttempt?.rolesWithHours && deleteAttempt.rolesWithHours.length > 1 ? 'these roles' : 'this role'}, you must:</strong>
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Reassign the logged hours to another role, or</li>
                <li>Remove the logged time entries from the time tracking system</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                This ensures data integrity between resource budgets and time tracking.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={cancelDeleteRole}>
              I Understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border rounded-none">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
          <h2 className="text-lg font-semibold">Budget Hours by Role</h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const { roleIndex, monthIndex } = selectedCell;
                  const role = byRole[roleIndex];
                  const hours = role.monthlyHours?.[monthKeys[monthIndex]] || 0;
                  // Validation: hours > 0 && more than one column
                  if (hours > 0 && totalMonths > 1) {
                    handleFillClick(roleIndex, monthIndex);
                  }
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (() => {
                if (!selectedCell || selectedCell.monthIndex === null) return true;
                const { roleIndex, monthIndex } = selectedCell;
                const role = byRole[roleIndex];
                if (!role) return true;
                const hours = role.monthlyHours?.[monthKeys[monthIndex]] || 0;
                // Disabled if: no value (hours === 0) OR only one column (totalMonths === 1)
                return hours === 0 || totalMonths === 1;
              })()}
              title="Fill from selected cell"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  // Single selection - use original handleDuplicateRole function
                  const selectedRoleId = Array.from(selectedRows)[0];
                  const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
                  if (roleIndex !== -1) handleDuplicateRole(roleIndex);
                } else if (selectedRows.size > 1) {
                  // Multiple selection - use new handleDuplicateMultipleRoles function
                  handleDuplicateMultipleRoles();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Duplicate ${selectedRows.size} selected roles` : "Duplicate role"}
            >
              <Files className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  // Use the selected cell's role
                  const roleIndex = selectedCell.roleIndex;
                  setShiftSource({ 
                    roleIndex, 
                    roleName: byRole[roleIndex].role 
                  });
                  setShiftDialogOpen(true);
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (byRole[selectedCell.roleIndex]?.monthlyHours?.[monthKeys[selectedCell.monthIndex]] ?? 0) === 0}
              title="Shift budget between months (select a monthly cell first)"
            >
              <MoveHorizontal className="h-4 w-4 mr-2" />
              Shift
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  // Single selection - use original removeRole function
                  const selectedRoleId = Array.from(selectedRows)[0];
                  const roleIndex = byRole.findIndex(r => r.id === selectedRoleId);
                  if (roleIndex !== -1) {
                    removeRole(roleIndex);
                  }
                } else if (selectedRows.size > 1) {
                  // Multiple selection - use new removeMultipleRoles function
                  removeMultipleRoles();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Delete ${selectedRows.size} selected roles` : "Delete selected role"}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={byRole.length >= roleOptionNames.length ? 0 : undefined}>
                    <Button 
                      onClick={addRole} 
                      size="sm" 
                      variant="outline"
                      disabled={roleOptionNames.length > 0 && byRole.length >= roleOptionNames.length}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Role
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{roleOptionNames.length > 0 && byRole.length >= roleOptionNames.length ? "No more available roles to add" : "Add a new role"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="p-0">
          {byRole.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No roles yet. Click "Add Role" to get started.
            </div>
          ) : (
            <>
              {/* 3-Column Layout: Left (sticky) + Middle (scrollable) + Right (sticky) */}
              <div className="flex">
              {/* LEFT SECTION: Checkbox + Role (Fixed, No Scroll) */}
              <div className="flex-shrink-0 border-r">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px] bg-background">
                        <Checkbox
                          checked={selectedRows.size === byRole.length && byRole.length > 0}
                          onCheckedChange={toggleAllRows}
                          aria-label="Select all rows"
                        />
                      </TableHead>
                      <TableHead className="w-[200px] bg-background">Role</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byRole.map((role, index) => (
                      <TableRow key={role.id}>
                        <TableCell className="p-2 w-[40px]">
                          <Checkbox
                            checked={selectedRows.has(role.id)}
                            onCheckedChange={() => toggleRowSelection(role.id)}
                            aria-label={`Select ${role.role}`}
                          />
                        </TableCell>
                        <TableCell className="p-2 w-[200px]">
                          <Combobox
                            options={(() => {
                              // Get roles already used by OTHER rows in this project
                              const usedRoles = byRole
                                .filter((r, i) => i !== index) // Exclude current row
                                .map(r => r.role);
                              
                              // Filter out already-used roles from options
                              const options = roleOptionNames
                                .filter(option => !usedRoles.includes(option))
                                .map((option) => ({
                                  value: option,
                                  label: option,
                                }));
                              
                              // Always include the current role if it's set (even if not in fetched roles)
                              if (role.role && !options.some(o => o.value === role.role)) {
                                options.unshift({ value: role.role, label: role.role });
                              }
                              return options;
                            })()}
                            value={role.role}
                            onValueChange={(value) => updateRole(index, 'role', value)}
                            placeholder="Select role"
                            searchPlaceholder="Search roles..."
                            emptyMessage="No role found."
                            className="h-9"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="p-2 w-[40px] bg-background"></TableCell>
                      <TableCell className="p-2 w-[200px] font-semibold bg-background">Project Total Hours</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              {/* MIDDLE SECTION: Monthly Columns (Scrollable) */}
              <div 
                className="flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)',
                }}
              >
                <Table 
                  className="border-collapse"
                  style={{
                    // Make table width match the number of months (100px per month)
                    width: `${yearMonths.length * 100}px`,
                    minWidth: '100%'
                  }}
                >
                  <TableHeader>
                    <TableRow>
                      {yearMonths.map((monthData) => (
                        <TableHead key={`${monthData.year}-${monthData.month}`} className="w-[100px] text-center">
                          {MONTHS[monthData.month]}/{String(monthData.year).slice(-2)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byRole.map((role, index) => (
                      <TableRow key={role.id}>
                        {visibleMonths.map((monthIndex) => {
                          const hours = role.monthlyHours?.[monthKeys[monthIndex]] ?? 0;
                          const isSelected = selectedCell?.roleIndex === index && selectedCell?.monthIndex === monthIndex;

                          return (
                            <TableCell key={monthIndex} className="p-2 w-[100px]">
                              <NumberInput
                                value={hours}
                                onChange={(value) => updateMonthlyHours(index, monthIndex, value)}
                                onCommit={onCellCommit}
                                maxValue={999999.99}
                                onFocus={() => setSelectedCell({ roleIndex: index, monthIndex })}
                                className={cn(
                                  "w-full h-9 px-2 text-sm text-center",
                                  isSelected && "ring-2 ring-ring"
                                )}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      {visibleMonths.map((monthIndex) => {
                        const monthTotal = byRole.reduce((sum, role) => {
                          return sum + (role.monthlyHours?.[monthKeys[monthIndex]] ?? 0);
                        }, 0);
                        return (
                          <TableCell key={monthIndex} className="p-2 w-[100px]">
                            <Input
                              value={formatNumber(monthTotal)}
                              readOnly
                              className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted cursor-default"
                              title={formatNumber(monthTotal)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>

              {/* RIGHT SECTION: Project Total (Fixed, No Scroll) */}
              <div className="flex-shrink-0 border-l-2">
                <Table className="border-collapse">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px] text-center font-semibold bg-primary/10">Project Total Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byRole.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="p-2 w-[120px]">
                          <Input
                            value={`${formatNumber(role.budgetHours)}h`}
                            readOnly
                            className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
                            title={`${formatNumber(role.budgetHours)}h`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="p-2 w-[120px] bg-background">
                        <Input
                          value={`${formatNumber(byRole.reduce((sum, role) => sum + role.budgetHours, 0))}h`}
                          readOnly
                          className="w-full h-9 px-2 text-sm text-center font-bold bg-primary/20 cursor-default"
                          title={`${formatNumber(byRole.reduce((sum, role) => sum + role.budgetHours, 0))}h`}
                        />
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <div className="px-4">
          <AISuggestion
            type="info"
            title="Pattern Detected"
            message={`Fill remaining months with ${patternSuggestion.value.toFixed(2)} hours?`}
            details={`You've entered ${patternSuggestion.value.toFixed(2)} hours for 3 consecutive months. Would you like to fill ${MONTHS[patternSuggestion.fromMonth]} through ${MONTHS[patternSuggestion.toMonth]} with the same value?`}
            confidence={95}
            onAccept={handlePatternAccept}
            onDismiss={handlePatternDismiss}
            acceptLabel="Fill Remaining Months"
            dismissLabel="No Thanks"
          />
        </div>
      )}

      {/* Fill Sheet (with expand to modal option) */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        sourceValue={fillSource?.value || 0}
        sourceMonth={fillSource?.monthIndex || 0}
        direction="right"
        onApply={handleFillApply}
        totalMonths={totalMonths}
        monthLabels={yearMonths.map(ym => `${MONTHS[ym.month]}/${String(ym.year).slice(-2)}`)}
        budgetType="resource"
      />
      
      {/* Original Fill Dialog (preserved for reference, not used) */}
      {/* <FillDialog
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        sourceValue={fillSource?.value || 0}
        sourceMonth={fillSource?.monthIndex || 0}
        direction="right"
        onApply={handleFillApply}
      /> */}

      {/* Shift Budget Sheet */}
      {shiftSource && (
        <ShiftBudgetSheet
          open={shiftDialogOpen}
          onOpenChange={setShiftDialogOpen}
          roleName={shiftSource.roleName}
          monthlyHours={monthKeys.map(k => byRole[shiftSource.roleIndex]?.monthlyHours?.[k] ?? 0)}
          selectedMonthIndex={selectedCell?.monthIndex ?? undefined}
          onShift={handleShiftBudget}
          totalMonths={totalMonths}
          monthLabels={yearMonths.map(ym => `${MONTHS[ym.month]}/${String(ym.year).slice(-2)}`)}
          budgetType="resource"
        />
      )}

    </div>
  );
}
